import React from 'react'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import {
  Typography,
  Chip,
  CircularProgress,
  Snackbar,
  LinearProgress
} from '@material-ui/core'
import {
  Tune as TuneIcon
} from '@material-ui/icons'
import DocumentUploadModal from './DocumentUploadModal'
import TakeoffRequestModal from './TakeoffRequestModal'
import ImportPreviewModal from './ImportPreviewModal'
import PlanPageSelector from './PlanPageSelector'
import VisualReviewModal from './VisualReviewModal'
import ExtractionRulesDrawer from './ExtractionRulesDrawer'
import DocumentFilterBar from './DocumentFilterBar'
import DocumentRow from './DocumentRow'
import { useBidConfirmation } from '../shared/BidConfirmationContext'

const formatElapsed = (value) => {
  const totalSeconds = Math.max(0, Math.floor(Number(value) || 0))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes <= 0) return `${seconds}s`
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`
}

const DocumentsSection = ({ projectId, project, url, onRefresh, onTriggerUpload, bidPackages, onDeleteBidPackage, onOpenContextDrawer }) => {
  const { openBidConfirmation } = useBidConfirmation()
  const history = useHistory()
  const hydratedProjectRef = React.useRef(null)
  const [documents, setDocuments] = React.useState(
    (project && Array.isArray(project.documents)) ? project.documents : []
  )
  const [loading, setLoading] = React.useState(false)
  const [showUploadModal, setShowUploadModal] = React.useState(false)
  const [importDoc, setImportDoc] = React.useState(null) // { _id, parseResult }
  const [pageSelectorDoc, setPageSelectorDoc] = React.useState(null) // doc with classified parseResult
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '' })
  const [takeoffDoc, setTakeoffDoc] = React.useState(null)
  const [rulesDrawer, setRulesDrawer] = React.useState({ open: false, mode: 'manager' })
  const [viewPagesDoc, setViewPagesDoc] = React.useState(null) // doc to view page images for
  const [pendingParseDocIds, setPendingParseDocIds] = React.useState(() => new Set())
  const [jobClock, setJobClock] = React.useState(() => Date.now())
  const [contextCounts, setContextCounts] = React.useState({})
  const [activeCategory, setActiveCategory] = React.useState('all')
  const [searchTerm, setSearchTerm] = React.useState('')

  const upsertLiveJobState = React.useCallback((docId, patch = {}) => {
    if (!docId) return
    setDocuments(prev => prev.map(doc => {
      if (String(doc._id) !== String(docId)) return doc
      const currentJob = doc.latestJob || {}
      const nextJob = {
        ...currentJob,
        ...patch,
        startedAt: patch.startedAt || currentJob.startedAt || new Date().toISOString()
      }
      return {
        ...doc,
        parseStatus: patch.parseStatus || doc.parseStatus,
        latestJob: nextJob
      }
    }))
  }, [])

  const trackPendingDoc = React.useCallback((docId) => {
    if (!docId) return
    setPendingParseDocIds(prev => {
      const next = new Set(prev)
      next.add(String(docId))
      return next
    })
  }, [])

  const untrackPendingDoc = React.useCallback((docId) => {
    if (!docId) return
    setPendingParseDocIds(prev => {
      const next = new Set(prev)
      next.delete(String(docId))
      return next
    })
  }, [])

  const fetchDocuments = React.useCallback(() => {
    if (!projectId) return
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents`).then(res => {
      setDocuments(Array.isArray(res.data) ? res.data : [])
      setLoading(false)
    }).catch(err => {
      console.error('[DocumentsSection] fetch error:', err)
      setLoading(false)
    })
  }, [projectId, url])

  React.useEffect(() => {
    const projectKey = String(projectId || '')
    const projectChanged = hydratedProjectRef.current !== projectKey

    if (project && Array.isArray(project.documents)) {
      if (projectChanged || documents.length === 0) {
        setDocuments(project.documents)
        hydratedProjectRef.current = projectKey
      }
      return
    }

    if (projectChanged || documents.length === 0) {
      fetchDocuments()
      hydratedProjectRef.current = projectKey
    }
  }, [projectId, project, fetchDocuments, documents.length])

  // Listen for socket events to auto-refresh when extraction completes
  React.useEffect(() => {
    if (!projectId) return
    try {
      const { subscribe } = require('../../SocketIo/Socket')
      const unsub = subscribe(`doc_upload_${projectId}`, (data) => {
        if (!data || !data.stage) return

        const eventDocId = data.docId || data.documentId
        const isTracked = eventDocId && pendingParseDocIds.has(String(eventDocId))
        const progressCurrent = Number(data.page || 0)
        const progressTotal = Number(data.total || 0)
        const nextStatus = data.stage === 'complete'
          ? (
              data.parseStatus === 'failed'
                ? 'failed'
                : data.parseStatus === 'parsed'
                  ? 'completed'
                  : data.parseStatus === 'classified'
                    ? 'completed'
                    : 'completed'
            )
          : 'running'

        if (eventDocId) {
          upsertLiveJobState(eventDocId, {
            status: nextStatus,
            stage: data.stage,
            message: data.message || '',
            progressCurrent,
            progressTotal,
            parseStatus: data.parseStatus || undefined
          })
        }

        if (data.stage === 'complete') {
          if (
            isTracked ||
            data.parseStatus === 'classified' ||
            data.parseStatus === 'parsed' ||
            data.parseStatus === 'failed'
          ) {
            fetchDocuments()
          }
          if (isTracked && eventDocId) untrackPendingDoc(eventDocId)
          if (
            isTracked ||
            data.parseStatus === 'classified' ||
            data.parseStatus === 'parsed' ||
            data.parseStatus === 'failed'
          ) {
            setSnackbar({
              open: true,
              message:
                data.message ||
                (data.parseStatus === 'classified'
                  ? 'Classification complete.'
                  : data.parseStatus === 'parsed'
                    ? 'Extraction complete.'
                    : 'Extraction failed.')
            })
          }
          return
        }

      })
      return unsub
    } catch (e) { /* socket not available */ }
  }, [projectId, fetchDocuments, onRefresh, pendingParseDocIds, untrackPendingDoc, upsertLiveJobState])


  // Fetch context counts for all documents in a single batch
  React.useEffect(() => {
    if (!documents?.length || !projectId) return
    const parents = documents.map(d => ({ parent_type: 'document', parent_id: d._id }))
    axios.post(`${url}/api/bid-estimation/projects/${projectId}/context/counts`, { parents })
      .then(res => setContextCounts(res.data))
      .catch(() => {})
  }, [documents, projectId, url])

  const handleUploaded = React.useCallback((newDoc) => {
    if (newDoc && newDoc._id) {
      setDocuments(prev => [...prev, {
        ...newDoc,
        latestJob: newDoc.parseStatus === 'pending'
          ? {
              status: 'running',
              stage: 'queued',
              message: 'Upload complete. Classifying pages in background...',
              progressCurrent: 0,
              progressTotal: 0,
              startedAt: new Date().toISOString()
            }
          : newDoc.latestJob
      }])
      if (newDoc.parseStatus === 'pending') {
        trackPendingDoc(newDoc._id)
        setSnackbar({
          open: true,
          message: 'Upload complete. Classifying pages in background...'
        })
      }
    } else {
      fetchDocuments()
    }
  }, [fetchDocuments, trackPendingDoc])

  React.useEffect(() => {
    if (typeof window === 'undefined' || !projectId) return undefined

    const handleDocumentUploadedEvent = (event) => {
      const detail = event?.detail
      if (!detail || String(detail.projectId) !== String(projectId)) return
      handleUploaded(detail.payload)
    }

    window.addEventListener('bid-estimation-document-uploaded', handleDocumentUploadedEvent)
    return () => window.removeEventListener('bid-estimation-document-uploaded', handleDocumentUploadedEvent)
  }, [projectId, handleUploaded])

  const handleDelete = (doc) => {
    if (!doc || !doc._id) return
    openBidConfirmation({
      title: 'Delete Document',
      message: `Delete "${doc.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      tone: 'danger',
      onConfirm: () => {
        axios.delete(`${url}/api/bid-estimation/projects/${projectId}/documents/${doc._id}`).then(() => {
          const deletedDocId = String(doc._id)
          setDocuments(prev => prev.filter(d => String(d._id) !== deletedDocId))
          setContextCounts(prev => {
            const next = { ...prev }
            delete next[`document:${deletedDocId}`]
            return next
          })
          untrackPendingDoc(deletedDocId)
          onRefresh && onRefresh()
          setSnackbar({ open: true, message: 'Document deleted' })
        }).catch(err => {
          console.error('[DocumentsSection] delete error:', err)
          setSnackbar({
            open: true,
            message: err.response?.data?.error || 'Failed to delete document'
          })
        })
      }
    })
  }

  const findVendorQuoteIdForDocument = React.useCallback(async (doc) => {
    if (doc.vendorQuoteId) return doc.vendorQuoteId

    const { data } = await axios.get(`${url}/api/bid-estimation/vendor-quotes/project/${projectId}`)
    const quotes = Array.isArray(data) ? data : []
    const match = quotes.find(q => (
      (doc.s3Key && q.s3Key && String(q.s3Key) === String(doc.s3Key)) ||
      (doc.name && q.originalFilename && String(q.originalFilename) === String(doc.name))
    ))
    return match?._id || null
  }, [projectId, url])

  const handleImportClick = async (doc) => {
    // Vendor quotes and invoices share the same review screen (QuoteReviewTable)
    if (doc.category === 'vendor_quote' || doc.category === 'vendor_invoice') {
      const docWord = doc.category === 'vendor_invoice' ? 'invoice' : 'quote'
      try {
        const quoteId = await findVendorQuoteIdForDocument(doc)
        if (!quoteId) {
          setSnackbar({ open: true, message: `Could not find linked vendor ${docWord}` })
          return
        }
        history.push(`/bid-estimation/${projectId}/vendor-quotes?quoteId=${quoteId}`)
      } catch (err) {
        console.error(`[DocumentsSection] vendor ${docWord} lookup error:`, err)
        setSnackbar({ open: true, message: `Could not open vendor ${docWord}` })
      }
      return
    }

    axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents/${doc._id}/view`).then(res => {
      setImportDoc(res.data)
    }).catch(err => {
      console.error('[DocumentsSection] document-view fetch error:', err)
      setSnackbar({ open: true, message: 'Could not load document view' })
    })
  }

  const handleOpenPageSelector = (doc) => {
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents/${doc._id}/view`).then(res => {
      const view = res.data || {}
      setPageSelectorDoc({
        ...doc,
        ...view.document,
        parseResult: view.parseResult || doc.parseResult || null,
        pages: Array.isArray(view.pages) ? view.pages : []
      })
    }).catch(err => {
      console.error('[DocumentsSection] document-view fetch error:', err)
      setSnackbar({ open: true, message: 'Could not load classified pages' })
    })
  }

  const handleImported = (count, result) => {
    const blockedCount = Array.isArray(result?.blockedRows) ? result.blockedRows.length : 0
    const suffix = blockedCount > 0
      ? `; ${blockedCount} row${blockedCount === 1 ? '' : 's'} left for review`
      : ''
    setSnackbar({ open: true, message: `Imported ${count} item${count === 1 ? '' : 's'}${suffix}` })
  }

  const handleQuickExtract = async (docId, pages) => {
    try {
      const { data } = await axios.post(
        `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/parse-pages`,
        { pages, postExtractMode: 'manual_review' }
      )
      setDocuments(prev => prev.map(d => d._id === docId
        ? {
            ...d,
            parseStatus: 'pending',
            latestJob: {
              ...(d.latestJob || {}),
              status: 'running',
              stage: 'queued',
              message: data?.message || `Extracting ${pages.length} pages in background...`,
              progressCurrent: 0,
              progressTotal: pages.length || 0,
              startedAt: new Date().toISOString()
            }
          }
        : d))
      trackPendingDoc(docId)
      setSnackbar({
        open: true,
        message: data?.message || `Extracting ${pages.length} pages in background...`
      })
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to start extraction' })
    }
  }

  const handlePagesParsed = (updatedDoc) => {
    fetchDocuments()
    setPageSelectorDoc(null)
    setSnackbar({ open: true, message: 'Pages extracted! Click "Import Items" to add them.' })
  }

  const handleParseStarted = ({ docId, pages, message }) => {
    if (!docId) return
    setDocuments(prev => prev.map(d => d._id === docId
      ? {
          ...d,
          parseStatus: 'pending',
          latestJob: {
            ...(d.latestJob || {}),
            status: 'running',
            stage: 'queued',
            message: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`,
            progressCurrent: 0,
            progressTotal: Array.isArray(pages) ? pages.length : 0,
            startedAt: new Date().toISOString()
          }
        }
      : d))
    trackPendingDoc(docId)
    setSnackbar({
      open: true,
      message: message || `Extracting ${Array.isArray(pages) ? pages.length : 0} pages in background...`
    })
  }

  const activeJobs = React.useMemo(() => {
    const now = jobClock
    return documents
      .map(doc => {
        const latestJob = doc.latestJob || null
        if (!latestJob) return null
        const status = String(latestJob.status || '').toLowerCase()
        const activeStatuses = new Set(['queued', 'running', 'leased', 'retrying', 'processing'])
        const isActive = activeStatuses.has(status) || doc.parseStatus === 'pending'
        if (!isActive) return null
        const startedAt = latestJob.startedAt ? new Date(latestJob.startedAt).getTime() : null
        const elapsedSeconds = startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0
        return {
          docId: doc._id,
          name: doc.name,
          parseStatus: doc.parseStatus,
          message: latestJob.message || 'Working...',
          stage: latestJob.stage || '',
          progressCurrent: Number(latestJob.progressCurrent || 0),
          progressTotal: Number(latestJob.progressTotal || 0),
          elapsedSeconds
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [documents, jobClock])

  React.useEffect(() => {
    if (activeJobs.length === 0) return undefined
    const timer = window.setInterval(() => {
      setJobClock(Date.now())
    }, 1000)
    return () => window.clearInterval(timer)
  }, [activeJobs.length])

  // Unified list — documents + bid-response packages — so the filter chips,
  // counts, and search all cover both. Bid packages map to category
  // 'bid_response'; real documents keep their own category.
  const listItems = [
    ...documents.map(d => ({
      kind: 'document', id: String(d._id),
      name: d.name || '', category: d.category || 'other', doc: d
    })),
    ...(Array.isArray(bidPackages) ? bidPackages : []).map(p => ({
      kind: 'bidPackage', id: `bp-${p._id}`,
      name: p.name || '', category: 'bid_response', pkg: p
    }))
  ]

  const visibleItems = listItems.filter(it => {
    if (activeCategory !== 'all' && (it.category || 'other') !== activeCategory) return false
    if (searchTerm && !it.name.toLowerCase().includes(searchTerm.toLowerCase())) return false
    return true
  })

  return (
    <div className="hmi-panel">
      <div className="hmi-panel-head">
        <h4>Documents{documents.length > 0 && <span style={{ fontWeight: 400, color: 'var(--ink-4)', marginLeft: 6 }}>({documents.length})</span>}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            className="hmi-btn hmi-btn-ghost hmi-btn-sm"
            onClick={() => setRulesDrawer({ open: true, mode: 'manager' })}
            title="Extraction Rules"
          >
            <TuneIcon style={{ fontSize: 15 }} />
          </button>
          <button
            className="hmi-btn hmi-btn-primary hmi-btn-sm"
            onClick={() => onTriggerUpload ? onTriggerUpload() : setShowUploadModal(true)}
          >
            + Upload
          </button>
        </div>
      </div>
      <div className="hmi-panel-body" style={{ padding: '8px 12px' }}>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <CircularProgress size={24} />
        </div>
      )}

      {!loading && documents.length === 0 && !(Array.isArray(bidPackages) && bidPackages.length > 0) && (
        <div
          onClick={() => onTriggerUpload ? onTriggerUpload() : setShowUploadModal(true)}
          style={{
            padding: '28px 16px',
            textAlign: 'center',
            color: 'var(--ink-4)',
            border: '1px dashed var(--line-2, #d0d0d0)',
            borderRadius: 6,
            cursor: 'pointer',
            margin: '4px 0'
          }}
        >
          <div style={{ fontSize: 13 }}>No documents yet — click to upload</div>
        </div>
      )}

      {!loading && listItems.length > 0 && (
        <DocumentFilterBar
          items={listItems}
          activeCategory={activeCategory}
          searchTerm={searchTerm}
          onCategoryChange={setActiveCategory}
          onSearchChange={setSearchTerm}
        />
      )}

      {!loading && listItems.length > 0 && (
        // Dense table — documents + bid packages unified. ~420px tall then scrolls.
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {visibleItems.length === 0 ? (
            <div style={{ padding: '20px 8px', textAlign: 'center', color: 'var(--ink-4)', fontSize: 12.5 }}>
              No documents match this filter.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-4)' }}>Name</th>
                  <th style={{ padding: '4px 8px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-4)', width: 110 }}>Status</th>
                  <th style={{ padding: '4px 8px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-4)', width: 200 }}>Details</th>
                  <th style={{ padding: '4px 8px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-4)', textAlign: 'right', width: 56 }} />
                </tr>
              </thead>
              <tbody>
                {visibleItems.map(item => (
                  <DocumentRow
                    key={item.id}
                    item={item}
                    contextCount={
                      contextCounts[
                        `document:${item.kind === 'document' ? item.doc._id : item.pkg._id}`
                      ]
                    }
                    latestJobMessage={
                      item.kind === 'document' &&
                      item.doc.parseStatus === 'pending' &&
                      item.doc.latestJob && item.doc.latestJob.message
                        ? item.doc.latestJob.message +
                          (item.doc.latestJob.progressTotal > 0
                            ? ` (${item.doc.latestJob.progressCurrent || 0}/${item.doc.latestJob.progressTotal})`
                            : '')
                        : null
                    }
                    onPrimaryAction={handleImportClick}
                    onSelectPages={handleOpenPageSelector}
                    onViewPages={setViewPagesDoc}
                    onMapIt={(d) => history.push(`/bid-estimation/${projectId}/plan-takeoff/${d._id}`)}
                    onRequestTakeoff={setTakeoffDoc}
                    onDelete={handleDelete}
                    onReviewBidPackage={(pkg) => history.push(`/bid-estimation/${projectId}/bid-response/${pkg._id}`)}
                    onDeleteBidPackage={onDeleteBidPackage}
                    onOpenContextDrawer={onOpenContextDrawer}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      </div>

      <DocumentUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploaded={handleUploaded}
        projectId={projectId}
        url={url}
      />

      {importDoc && (
        <ImportPreviewModal
          open={Boolean(importDoc)}
          onClose={() => setImportDoc(null)}
          onParseStarted={handleParseStarted}
          onImported={(count, result) => {
            setImportDoc(null)
            handleImported(count, result)
          }}
          projectId={projectId}
          docId={importDoc.document?._id || importDoc._id}
          parseResult={importDoc.parseResult}
          documentView={importDoc}
          project={project}
          url={url}
        />
      )}

      {pageSelectorDoc && (
        <PlanPageSelector
          open={Boolean(pageSelectorDoc)}
          onClose={() => setPageSelectorDoc(null)}
          doc={pageSelectorDoc}
          projectId={projectId}
          url={url}
          onParsed={handlePagesParsed}
          onParseStarted={handleParseStarted}
        />
      )}

      <TakeoffRequestModal
        open={!!takeoffDoc}
        onClose={(action, count) => {
          setTakeoffDoc(null)
          if (action === 'sent') {
            setSnackbar({ open: true, message: `Takeoff requests sent to ${count} vendor${count !== 1 ? 's' : ''}` })
          }
        }}
        doc={takeoffDoc}
        projectId={projectId}
        project={project}
        url={url}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {activeJobs.length > 0 && (
        <div
          style={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            width: 380,
            maxWidth: 'calc(100vw - 32px)',
            zIndex: 1400,
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid #eeeeee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              Processing Jobs
            </Typography>
            <Chip
              size="small"
              label={`${activeJobs.length} active`}
              style={{ backgroundColor: '#e3f2fd', color: '#1565c0', height: 22 }}
            />
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {activeJobs.map(job => {
              const hasProgress = job.progressTotal > 0
              const progressValue = hasProgress
                ? Math.max(0, Math.min(100, (job.progressCurrent / job.progressTotal) * 100))
                : null
              return (
                <div
                  key={job.docId}
                  style={{
                    padding: '12px 14px',
                    borderBottom: '1px solid #f3f3f3'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {job.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {job.message}
                      </Typography>
                    </div>
                    <Typography variant="caption" color="textSecondary" style={{ whiteSpace: 'nowrap' }}>
                      {formatElapsed(job.elapsedSeconds)}
                    </Typography>
                  </div>
                  {hasProgress && (
                    <div style={{ marginTop: 8 }}>
                      <LinearProgress variant="determinate" value={progressValue} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                        <Typography variant="caption" color="textSecondary">
                          {job.stage || 'running'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {job.progressCurrent}/{job.progressTotal}
                        </Typography>
                      </div>
                    </div>
                  )}
                  {!hasProgress && (
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
                      {job.stage || 'running'}
                    </Typography>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {viewPagesDoc && (
        <VisualReviewModal
          open={Boolean(viewPagesDoc)}
          onClose={() => setViewPagesDoc(null)}
          sheetIndex={(viewPagesDoc.parseResult && viewPagesDoc.parseResult.sheetIndex) || []}
          selected={new Set()}
          projectId={projectId}
          docId={viewPagesDoc._id}
          url={url}
          readOnly
        />
      )}

      <ExtractionRulesDrawer
        open={rulesDrawer.open}
        onClose={() => setRulesDrawer({ open: false, mode: 'manager' })}
        url={url}
        mode={rulesDrawer.mode}
        projectId={rulesDrawer.projectId || projectId}
        docId={rulesDrawer.docId}
        page={rulesDrawer.page}
        discipline={rulesDrawer.discipline}
        extractedItems={rulesDrawer.extractedItems}
      />
    </div>
  )
}

export default DocumentsSection
